import { createContext, useContext, useEffect } from 'react';

/** A handler for every file carried by one window-level drop. */
export type WindowDropFilesHandler = (files: File[]) => void;

/**
 * Registry the active mode's own drop flow plugs into (cue-forge-w28.14). Both
 * mode panels stay mounted (drt.14), so the window-level net can't just assume
 * one route exists: the shell keeps the registered handler in a ref and decides
 * per drop — Convert mode delegates to the Converter's own intake (batch-discard
 * confirm, multi-file → batch list), Studio keeps the plain session load.
 */
export interface WindowDropDelegate {
  set: (handler: WindowDropFilesHandler | null) => void;
}

export const WindowDropDelegateContext = createContext<WindowDropDelegate | null>(null);

/**
 * Studio's shared file intake — the AppShell's window-net drop handler, exposed
 * so Studio's own dropzone (the EmptyState) can route a multi-file drop through
 * the exact same load + "N more files were ignored" notice plumbing instead of
 * silently discarding files[1..] a few pixels from where the net reports them.
 * One handler, ONE live region (the shell's drop notice). Null outside the shell
 * (unit tests) — callers fall back to a plain single-file load.
 */
export const StudioFileDropContext = createContext<WindowDropFilesHandler | null>(null);

/**
 * Register a mode's own file-intake flow as the window-drop delegate. Re-runs
 * every render (no deps) so the registered closure is never stale; unregisters
 * on unmount. A component rendered without the provider (unit tests) no-ops.
 */
export function useWindowDropDelegate(handler: WindowDropFilesHandler): void {
  const delegate = useContext(WindowDropDelegateContext);
  useEffect(() => {
    if (!delegate) return;
    delegate.set(handler);
    return () => delegate.set(null);
  });
}

/**
 * Window-level file-drop safety net (cue-forge-drt.7). Dropping a file anywhere
 * the app doesn't explicitly handle triggers the browser default — it navigates
 * to the file and destroys the session (and beforeunload only fires while dirty,
 * so a clean just-QC'd doc dies silently). This guards the whole window: it
 * preventDefaults file dragover/drop and routes ALL dropped files to `onFiles`
 * (cue-forge-w28.14 — the caller decides mode-appropriate multi-file semantics;
 * a multi-drop must never silently collapse to its first file). Dedicated drop
 * zones (the EmptyState/Converter dropzones) call preventDefault in their own
 * handler, so this net skips them via `e.defaultPrevented`.
 */
export function useWindowFileDrop(onFiles: WindowDropFilesHandler): void {
  useEffect(() => {
    const onDragOver = (e: DragEvent) => {
      if (e.dataTransfer?.types.includes('Files')) e.preventDefault();
    };
    const onDrop = (e: DragEvent) => {
      if (e.defaultPrevented) return; // a component's own drop zone already handled it
      const files = e.dataTransfer ? Array.from(e.dataTransfer.files) : [];
      if (files.length === 0) return; // not a file drag (e.g. text selection) — leave it alone
      e.preventDefault();
      onFiles(files);
    };
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('drop', onDrop);
    };
  }, [onFiles]);
}
